/**
 * The last resort: the device's own voice.
 *
 * Miles asked for Prest0n's real voice, and that is what the control uses. This
 * exists for the moment the real one cannot be had — offline, a transport
 * failure, every rung on the tier-10 ladder down — because a control that
 * dead-ends is worse than a plainer voice. It is NEVER the first choice, is
 * never used for a refusal the server made deliberately (no permission, the
 * daily ceiling, an empty wallet: those are answers, not failures), and it
 * costs nothing because nothing leaves the browser.
 *
 * The flatter voice is the tell that something went wrong, which is honest.
 */

interface SpeechWindow {
  speechSynthesis?: SpeechSynthesis;
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance;
}

function host(): SpeechWindow | null {
  return typeof window === 'undefined' ? null : (window as unknown as SpeechWindow);
}

export function deviceVoiceAvailable(): boolean {
  const w = host();
  return Boolean(w?.speechSynthesis && w?.SpeechSynthesisUtterance);
}

/**
 * Speak `text` with the device voice. Resolves when it finishes, is cancelled,
 * or fails — the caller treats all three the same way (back to idle).
 */
export function speakWithDeviceVoice(text: string, language?: string): Promise<void> {
  const w = host();
  if (!w?.speechSynthesis || !w.SpeechSynthesisUtterance || !text.trim()) return Promise.resolve();
  return new Promise<void>((resolve) => {
    try {
      w.speechSynthesis!.cancel();
      const utterance = new w.SpeechSynthesisUtterance!(text);
      if (language) utterance.lang = language === 'es' ? 'es-ES' : 'en-US';
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      utterance.onend = done;
      utterance.onerror = done;
      w.speechSynthesis!.speak(utterance);
    } catch {
      resolve();
    }
  });
}

export function cancelDeviceVoice(): void {
  try {
    host()?.speechSynthesis?.cancel();
  } catch {
    /* nothing to cancel */
  }
}
